import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { HardHat, LayoutDashboard, Building2, Ticket, Users2, Calendar, FileText, LogOut, ChevronLeft, Menu, ShieldCheck, KeyRound, Contact, HardHatIcon, Wrench, Activity, Sparkles, Calculator, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import NotificationBell from "@/components/NotificationBell";
import Breadcrumb from "@/components/Breadcrumb";

function NavItem({ to, icon: Icon, label, active, onClick, testid }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
        active
          ? "bg-amber-500/15 text-amber-400 border-l-4 border-amber-500 pl-3"
          : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100"
      }`}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
    </Link>
  );
}

function SidebarContent({ onNavigate }) {
  const { user } = useAuth();
  const location = useLocation();
  const { groupId } = useParams();
  const path = location.pathname;
  const isSubUser = user?.role === "SUBAPPALTATORE";

  const base = groupId ? `/g/${groupId}` : "";
  const groupNav = groupId ? [
    { to: `${base}`, icon: LayoutDashboard, label: "Dashboard gruppo", test: "nav-group-home" },
    { to: `${base}/cantieri`, icon: Building2, label: "Cantieri", test: "nav-cantieri" },
    { to: `${base}/ticket-tecnici`, icon: Ticket, label: "Ticket tecnici", test: "nav-ticket-tecnici" },
    { to: `${base}/ticket-subappaltatori`, icon: Users2, label: "Ticket subappaltatori", test: "nav-ticket-sub" },
    { to: `${base}/calendario`, icon: Calendar, label: "Calendario materiali", test: "nav-calendario" },
    { to: `${base}/documenti`, icon: FileText, label: "Documenti", test: "nav-documenti" },
  ] : [];

  return (
    <div className="h-full flex flex-col bg-slate-950 text-slate-100">
      <Link to={isSubUser ? "/portale/ticket" : "/"} onClick={onNavigate} className="flex items-center gap-2 px-5 py-5 border-b border-slate-800">
        <div className="w-9 h-9 bg-amber-500 rounded-lg flex items-center justify-center">
          <HardHat className="h-5 w-5 text-slate-950" />
        </div>
        <div>
          <div className="text-sm font-bold tracking-tight">{isSubUser ? "Portale Subappaltatore" : "Gestione Cantieri"}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{isSubUser ? "AREA RISERVATA" : "MODULO 1"}</div>
        </div>
      </Link>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {isSubUser ? (
          <>
            <NavItem to="/portale/ticket" icon={Ticket} label="I miei ticket" active={path.startsWith("/portale/ticket")} onClick={onNavigate} testid="nav-portale-ticket" />
            {user?.can_view_cantieri && (
              <NavItem to="/portale/cantieri" icon={Building2} label="I miei cantieri" active={path.startsWith("/portale/cantieri")} onClick={onNavigate} testid="nav-portale-cantieri" />
            )}
          </>
        ) : (
          <>
            <NavItem to="/" icon={LayoutDashboard} label="Gruppi cantieri" active={path === "/"} onClick={onNavigate} testid="nav-groups" />
            <NavItem to="/quaderno" icon={BookOpen} label="Quaderno" active={path.startsWith("/quaderno")} onClick={onNavigate} testid="nav-quaderno" />
            <NavItem to="/subappaltatori" icon={Wrench} label="Subappaltatori" active={path.startsWith("/subappaltatori")} onClick={onNavigate} testid="nav-subappaltatori" />
            <NavItem to="/andamento" icon={Activity} label="Andamento cantieri" active={path.startsWith("/andamento")} onClick={onNavigate} testid="nav-andamento" />
            <NavItem to="/analisi-prezzi" icon={Calculator} label="Analisi Prezzi" active={path.startsWith("/analisi-prezzi")} onClick={onNavigate} testid="nav-analisi-prezzi" />
            {user?.role === "ADMIN" && (
              <NavItem to="/assistant" icon={Sparkles} label="Assistente INVILUPPO" active={path.startsWith("/assistant")} onClick={onNavigate} testid="nav-assistant" />
            )}
            {groupId && (
              <>
                <div className="pt-4 pb-1 px-4 text-[10px] uppercase tracking-widest text-slate-600">Gruppo corrente</div>
                {groupNav.map((n) => (
                  <NavItem key={n.to} to={n.to} icon={n.icon} label={n.label} active={path === n.to || (n.to !== base && path.startsWith(n.to))} onClick={onNavigate} testid={n.test} />
                ))}
              </>
            )}
            {user?.role === "ADMIN" && (
              <>
                <div className="pt-4 pb-1 px-4 text-[10px] uppercase tracking-widest text-slate-600">Amministrazione</div>
                <NavItem to="/admin/users" icon={ShieldCheck} label="Utenti & permessi" active={path.startsWith("/admin/users")} onClick={onNavigate} testid="nav-admin-users" />
                <NavItem to="/admin/anagrafica" icon={Contact} label="Anagrafica persone" active={path === "/admin/anagrafica"} onClick={onNavigate} testid="nav-admin-anagrafica" />
                <NavItem to="/admin/anagrafica-subappaltatori" icon={Building2} label="Anagrafica subappaltatori" active={path.startsWith("/admin/anagrafica-subappaltatori")} onClick={onNavigate} testid="nav-admin-anagrafica-sub" />
              </>
            )}
          </>
        )}
      </nav>
      <div className="px-3 py-3 border-t border-slate-800 text-xs text-slate-500">
        Postgres · Sicurezza JWT + cookie httpOnly
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { groupId } = useParams();
  const [mobileOpen, setMobileOpen] = useState(false);
  const initials = user?.full_name?.split(" ").map(s => s[0]).slice(0,2).join("").toUpperCase() || "?";

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 border-r border-slate-800">
        <SidebarContent />
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between px-4 lg:px-8 h-14 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64 bg-slate-950 border-slate-800">
                <SidebarContent onNavigate={closeMobile} />
              </SheetContent>
            </Sheet>
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn" className="hidden sm:flex">
              <ChevronLeft className="h-4 w-4 mr-1" /> Indietro
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell groupId={groupId} />
            <span className="hidden sm:inline text-xs text-slate-500 dark:text-slate-400">{user?.role}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                  <Avatar className="h-7 w-7 bg-amber-500"><AvatarFallback className="bg-amber-500 text-slate-950 text-xs font-bold">{initials}</AvatarFallback></Avatar>
                  <span className="hidden sm:inline text-sm">{user?.full_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/change-password")} data-testid="change-password-menu">
                  <KeyRound className="h-4 w-4 mr-2" /> Cambia password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-menu" className="text-rose-600">
                  <LogOut className="h-4 w-4 mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="p-4 lg:p-8">
          <Breadcrumb />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
